import {
  type Control,
  type FieldValues,
  type FormState,
  type Path,
  type PathValue,
  type UseFormRegister,
  type UseFormSetValue,
  useWatch,
} from "react-hook-form";

import { Button, Paragraph } from "@fronterahealth/frontera-ui-components";

import { FormInput } from "@components/forms/FormInput";
import {
  PROVIDER_PROFILE_FIELDS,
  type ProviderProfileValues,
  isProviderProfileFieldChanged,
} from "@pages/Organizations/providerProfile";

interface ProviderProfileFieldsProps<T extends FieldValues & ProviderProfileValues> {
  register: UseFormRegister<T>;
  formState: FormState<T>;
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  original: ProviderProfileValues;
  /** Edit mode: mark fields that differ from `original`, with a revert, so a change is never made by accident. */
  highlightChanges: boolean;
}

export const ProviderProfileFields = <T extends FieldValues & ProviderProfileValues>({
  register,
  formState,
  control,
  setValue,
  original,
  highlightChanges,
}: ProviderProfileFieldsProps<T>) => {
  // Watched here rather than in the parent form so typing only re-renders this section.
  const values = useWatch({ control, name: PROVIDER_PROFILE_FIELDS.map(({ key }) => key as Path<T>) });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Paragraph displayType="loud" size="small" colorType="primary">
          Professional details
        </Paragraph>
        <Paragraph size="small" colorType="secondary">
          Title and license # appear on this provider&apos;s report signatures.
        </Paragraph>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {PROVIDER_PROFILE_FIELDS.map(({ key, label, placeholder }, i) => {
          const previous = original[key];
          const isChanged = highlightChanges && isProviderProfileFieldChanged(values[i], previous);
          return (
            <div key={key}>
              {/* FormInput rather than RegisteredFormInput: the latter's identity changes with formState,
                  which remounts these inputs (losing focus) whenever the parent form re-renders. */}
              <FormInput<T>
                register={register}
                formState={formState}
                formKey={key as Path<T>}
                label={label}
                placeholder={placeholder}
                required={false}
                // Optional and unvalidated, so skip the Input's reserved error line.
                formNoValidate
                // No Mosaic border-warning token yet; icon-warning is the closest yellow.
                inputClassName={isChanged ? "ring-2! ring-icon-warning!" : undefined}
              />
              {isChanged && (
                <div className="mt-1 flex items-center gap-2">
                  <Paragraph size="small" className="text-icon-warning!">
                    Was: {previous ? `"${previous}"` : "blank"}
                  </Paragraph>
                  <Button
                    type="button"
                    variant="primary"
                    buttonStyle="ghost"
                    size="xs"
                    text="Revert"
                    aria-label={`Revert ${label}`}
                    onClick={() => {
                      setValue(key as Path<T>, previous as PathValue<T, Path<T>>);
                      // Revert unmounts itself, so hand focus back to the restored input (Input's id is its name).
                      document.getElementById(key)?.focus();
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
